import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  listSessionSchedules, listAttendanceActiveCustomers, listAttendanceToday,
  checkInAttendance, undoCheckInAttendance, listRoster, getScanFailures,
} from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dumbbell, Check, X, Search, UserCheck, AlertCircle, Clock, Maximize2, Minimize2, ScanLine, Users } from "lucide-react";

// No session needed (22 Oct): "they're at the gym today", once a day. The server treats this id specially.
const GENERAL = "general";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const todayISO = () => {
  // Use the *local* date so it matches todayDOW()/the facility's wall clock.
  // toISOString() would drift to the previous/next day near midnight in non-UTC
  // zones and mis-bucket attendance.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const nowMinutes = () => {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
};
const hhmmToMinutes = (s) => {
  const [h, m] = (s || "0:0").split(":").map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
};
const todayDOW = () => (new Date().getDay() + 6) % 7;  // Mon=0
// Anyone who can run a session can be its lead coach, part timers included (not just Accountability).
const COACHING_SKILLS = ["accountability", "large_group_coach", "PT", "SPT"];

export default function CheckIn() {
  const askDelete = useDeleteAsk("manager");
  const { activeFacility } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const kiosk = searchParams.get("kiosk") === "1";
  const [schedules, setSchedules] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [sel, setSel] = useState(null);           // session id
  const [leadCoach, setLeadCoach] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [everyone, setEveryone] = useState({ attendance: [], members: 0 });   // all check-ins on the date
  const [noScan, setNoScan] = useState([]);                                    // members whose app scan failed
  const searchRef = useRef(null);

  // Initial loads
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, c, r] = await Promise.all([
          listSessionSchedules(),
          listAttendanceActiveCustomers(),
          listRoster().catch(() => []),
        ]);
        setSchedules(s);
        setCustomers(c);
        setCoaches((r || []).filter((p) => (p.skills || []).some((k) => COACHING_SKILLS.includes(k))));
      } catch {
        toast.error("Could not load check-in data");
      } finally {
        setLoading(false);
      }
    })();
  }, [activeFacility?.id]);

  // The coach first confirms the date (usually today), then picks from the
  // sessions scheduled for THAT day only — no full-week clutter.
  const [dateISO, setDateISO] = useState(todayISO());
  const selDOW = useMemo(() => {
    const d = new Date(`${dateISO}T00:00:00`);
    return isNaN(d.getTime()) ? todayDOW() : (d.getDay() + 6) % 7; // Mon=0
  }, [dateISO]);

  const daySessions = useMemo(
    () => (schedules || [])
      .filter((s) => s.day_of_week === selDOW && s.active !== false)
      .slice()
      .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time))),
    [schedules, selDOW],
  );

  // Auto-pick nearest session (within ±2 hrs) — only for today's date.
  useEffect(() => {
    if (dateISO !== todayISO() || sel || daySessions.length === 0) return;
    const now = nowMinutes();
    let best = null, bestDelta = Infinity;
    for (const s of daySessions) {
      const delta = Math.abs(hhmmToMinutes(s.start_time) - now);
      if (delta < bestDelta) { bestDelta = delta; best = s; }
    }
    setSel(best && bestDelta <= 120 ? best.id : GENERAL);
  }, [daySessions, sel, dateISO]);
  useEffect(() => { if (!sel && (dateISO !== todayISO() || daySessions.length === 0)) setSel(GENERAL); }, [sel, dateISO, daySessions]);

  const changeDate = (v) => { setDateISO(v); setSel(null); };

  const loadAttendees = useCallback(async (sid, d) => {
    if (!sid) return setAttendees([]);
    try {
      const res = await listAttendanceToday(sid, d);
      setAttendees(res.attendance || []);
    } catch {
      setAttendees([]);
    }
  }, []);
  useEffect(() => { loadAttendees(sel === GENERAL ? null : sel, dateISO); }, [sel, dateISO, loadAttendees]);

  // Everyone checked in on the date, however they got in, plus members who couldn't scan in the app.
  const loadDay = useCallback(async () => {
    try { const r = await listAttendanceToday(null, dateISO); setEveryone({ attendance: r.attendance || [], members: r.members || 0 }); }
    catch { setEveryone({ attendance: [], members: 0 }); }
    try { setNoScan(((await getScanFailures("member", dateISO)).people || []).filter((p) => !p.got_in)); }
    catch { setNoScan([]); }
  }, [dateISO]);
  useEffect(() => { loadDay(); const t = setInterval(loadDay, 60000); return () => clearInterval(t); }, [loadDay]);

  const selSchedule = useMemo(() => schedules.find((s) => s.id === sel) || null, [schedules, sel]);
  const takenKeys = useMemo(() => new Set((sel === GENERAL ? everyone.attendance : attendees).map((a) => a.customer_key)),
    [attendees, everyone, sel]);

  const filteredCustomers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return customers
      .filter((c) => {
        const hay = `${c.name || ""} ${c.phone || ""} ${c.email || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 12);
  }, [customers, query]);

  const addCustomer = async (c) => {
    if (sel !== GENERAL && !leadCoach.trim()) { toast.error("Enter the lead coach first"); return; }
    if (!sel) { toast.error("Pick a session first"); return; }
    if (takenKeys.has(c.customer_key)) { toast.info(`${c.name} already checked in`); return; }
    try {
      const res = await checkInAttendance({
        session_schedule_id: sel,
        session_date: dateISO,
        lead_coach: leadCoach.trim(),
        customer_key: c.customer_key,
        customer_name: c.name,
        customer_phone: c.phone,
      });
      if (res.duplicate) toast.info(`${c.name} already checked in`);
      else toast.success(`${c.name} checked in`);
      setAttendees((prev) => [res.attendance, ...(prev || []).filter((a) => a.id !== res.attendance.id)]);
      loadDay();
      setQuery("");
      searchRef.current?.focus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check in");
    }
  };

  const removeAttendee = async (a) => {
    const reason = askDelete(`${a.customer_name}'s check-in`, `Remove ${a.customer_name} from this session?`);
    if (reason === null) return;
    try {
      if (deletionRequested(await undoCheckInAttendance(a.id, reason))) return;
      setAttendees((prev) => (prev || []).filter((x) => x.id !== a.id));
      loadDay();
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  const enterKiosk = () => {
    setSearchParams({ kiosk: "1" });
    // Fullscreen needs a user gesture — this click qualifies. Non-fatal if denied.
    document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const exitKiosk = () => {
    setSearchParams({});
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  };

  return (
    <div className={kiosk ? "mx-auto max-w-5xl space-y-6 px-4 py-6" : "space-y-6"} data-testid="check-in-page">
      {kiosk && (
        <div className="flex items-center justify-between rounded-xl border border-[#1f1f1f] bg-[#0b0b0b] px-4 py-3" data-testid="kiosk-bar">
          <div className="flex items-center gap-2 text-sm text-zinc-300">
            <Dumbbell className="h-4 w-4 text-emerald-400" />
            <span className="font-semibold">{activeFacility?.name || "Facility"}</span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-400">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</span>
          </div>
          <Button variant="outline" size="sm" onClick={exitKiosk} data-testid="kiosk-exit-btn">
            <Minimize2 className="mr-1.5 h-3.5 w-3.5" /> Exit kiosk
          </Button>
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Tablet mode · Front-of-house</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Session Check-In</h2>
          {!kiosk && (
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Members can scan the desk QR in the LIFTR app, or a coach checks them in here: pick a session (or General check-in) and tap each member as they walk in. Attendance feeds the weekly no-show follow-up queue.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!kiosk && (
            <Button variant="outline" size="sm" onClick={enterKiosk} data-testid="kiosk-enter-btn">
              <Maximize2 className="mr-1.5 h-3.5 w-3.5" /> Kiosk mode
            </Button>
          )}
          {!kiosk && (
            <div className="text-right text-xs text-zinc-500">
              {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            </div>
          )}
        </div>
      </div>

      {/* 1. SESSION PICKER — date first, then only that day's sessions */}
      <section>
        <div className="eyebrow mb-2 flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-emerald-400" /> 1 · Session</div>
        {(
          <div className="card-surface p-4" data-testid="session-picker">
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Date</div>
                <Input
                  type="date"
                  value={dateISO}
                  onChange={(e) => changeDate(e.target.value)}
                  className="h-12 w-44 text-lg"
                  data-testid="session-date-input"
                />
              </div>
              <div className="min-w-64 flex-1">
                <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  {DAY_LABELS[selDOW]}&apos;s sessions
                </div>
                {(
                  <select
                    value={sel || ""}
                    onChange={(e) => setSel(e.target.value || null)}
                    className="h-12 w-full rounded-md border border-input bg-[#0a0a0a] px-3 text-lg text-zinc-100"
                    data-testid="session-select"
                  >
                    <option value={GENERAL} data-testid="session-option-general">General check-in (no session)</option>
                    {daySessions.map((s) => (
                      <option key={s.id} value={s.id} data-testid={`session-option-${s.id}`}>
                        {s.start_time} · {s.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* 2. LEAD COACH (sessions only) */}
      {sel && sel !== GENERAL && (
        <section>
          <div className="eyebrow mb-2 flex items-center gap-2"><UserCheck className="h-3.5 w-3.5 text-emerald-400" /> 2 · Lead coach</div>
          <div className="card-surface p-4">
            <Input
              data-testid="lead-coach-input"
              value={leadCoach}
              onChange={(e) => setLeadCoach(e.target.value)}
              placeholder="Who's running this session?"
              list="lead-coach-options"
              className="h-12 text-lg"
            />
            <datalist id="lead-coach-options">
              {coaches.map((c) => <option key={c.user_id || c.name} value={c.name} />)}
            </datalist>
          </div>
        </section>
      )}

      {/* 3. ADD CLIENTS */}
      {sel && (sel === GENERAL || leadCoach.trim()) && (
        <section>
          <div className="eyebrow mb-2 flex items-center gap-2"><Search className="h-3.5 w-3.5 text-emerald-400" /> {sel === GENERAL ? 2 : 3} · Check in members</div>
          <div className="card-surface p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <Input
                ref={searchRef}
                data-testid="client-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, phone or email…"
                className="h-12 pl-10 text-lg"
                autoFocus
              />
            </div>
            {query.trim() && (
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="client-results">
                {filteredCustomers.length === 0 && (
                  <div className="col-span-full text-sm text-zinc-500 py-2">No active member matches &quot;{query}&quot;.</div>
                )}
                {(filteredCustomers || []).map((c) => {
                  const taken = takenKeys.has(c.customer_key);
                  return (
                    <button
                      key={c.customer_key}
                      data-testid={`client-row-${c.customer_key}`}
                      onClick={() => addCustomer(c)}
                      disabled={taken}
                      className={`flex items-center justify-between rounded-lg border px-3 py-3 text-left transition-colors ${
                        taken ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300/70 cursor-not-allowed"
                              : "border-[#262626] bg-[#0d0d0d] text-zinc-100 hover:border-emerald-500/60"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">{c.name}</div>
                        <div className="truncate text-[11px] text-zinc-500">
                          {c.phone || c.email || ""}{c.programme ? ` · ${c.programme}` : ""}
                        </div>
                      </div>
                      {taken ? <Check className="h-5 w-5 shrink-0" /> : <span className="rounded-full bg-emerald-500 px-2 py-1 text-xs font-semibold text-black">Check in</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {!query.trim() && customers.length === 0 && !loading && (
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-300">
                <AlertCircle className="h-3.5 w-3.5" /> No active members for this facility — add sales with an un-expired <span className="text-zinc-300">end_date</span> first.
              </div>
            )}
          </div>
        </section>
      )}

      {/* MEMBERS WHO COULDN'T SCAN IN THE APP */}
      {noScan.length > 0 && (
        <section data-testid="no-scan-members">
          <div className="eyebrow mb-2 flex items-center gap-2 text-amber-300">
            <ScanLine className="h-3.5 w-3.5" /> Couldn&apos;t scan in the app
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-mono text-[10px]">{noScan.length}</span>
          </div>
          <div className="card-surface overflow-hidden">
            <ul className="divide-y divide-[#1a1a1a]">
              {noScan.map((p) => (
                <li key={p.person_id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-zinc-100">{p.name || p.phone || "Member"}</div>
                    <div className="truncate text-[11px] text-zinc-500">
                      {new Date(p.last_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                      {p.tries > 1 ? ` · ${p.tries} tries` : ""} · {p.reason}
                    </div>
                  </div>
                  <Button size="sm" data-testid={`noscan-checkin-${p.person_id}`}
                    onClick={async () => {
                      try {
                        await checkInAttendance({ session_schedule_id: GENERAL, session_date: dateISO, customer_key: p.person_id,
                          customer_name: p.name, customer_phone: p.phone });
                        toast.success(`${p.name || "Member"} checked in`);
                        loadDay();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Could not check in"); }
                    }}>Check in</Button>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* 4. SESSION ROLL CALL */}
      {sel && sel !== GENERAL && (
        <section>
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Dumbbell className="h-3.5 w-3.5 text-emerald-400" />
            {dateISO === todayISO() ? "Today\u2019s roll call" : `Roll call · ${new Date(`${dateISO}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}`} · {selSchedule?.label}
            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-mono text-[10px] text-emerald-300" data-testid="attendee-count">
              {attendees.length}
            </span>
          </div>
          <div className="card-surface overflow-hidden">
            {attendees.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">No check-ins yet.</div>
            ) : (
              <ul className="divide-y divide-[#1a1a1a]">
                {attendees.map((a) => (
                  <li key={a.id} className="flex items-center justify-between px-4 py-3" data-testid={`attendee-${a.id}`}>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-zinc-100">{a.customer_name}</div>
                      <div className="truncate text-[11px] text-zinc-500">
                        {new Date(a.checked_in_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                        {" · "}by {a.checked_in_by_name || "coach"}
                        {a.lead_coach ? ` · lead: ${a.lead_coach}` : ""}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" data-testid={`remove-${a.id}`} onClick={() => removeAttendee(a)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* EVERYONE CHECKED IN ON THE DATE, HOWEVER THEY GOT IN */}
      <section data-testid="everyone-checked-in">
        <div className="eyebrow mb-2 flex items-center gap-2">
          <Users className="h-3.5 w-3.5 text-emerald-400" />
          Everyone checked in · {dateISO === todayISO() ? "today" : new Date(`${dateISO}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-mono text-[10px] text-emerald-300" data-testid="everyone-count">
            {everyone.members}
          </span>
        </div>
        <div className="card-surface overflow-hidden">
          {everyone.attendance.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">Nobody checked in yet.</div>
          ) : (
            <ul className="divide-y divide-[#1a1a1a]">
              {everyone.attendance.map((a) => (
                <li key={a.id} className="flex items-center justify-between px-4 py-3" data-testid={`everyone-${a.id}`}>
                  <div className="min-w-0">
                    <div className="truncate font-medium text-zinc-100">{a.customer_name || a.customer_key}</div>
                    <div className="truncate text-[11px] text-zinc-500">
                      {new Date(a.checked_in_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                      {" · "}{a.how}{a.checked_in_by_name && a.how !== "Scanned in the app" ? ` (${a.checked_in_by_name})` : ""}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeAttendee(a)}><X className="h-4 w-4" /></Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
